import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  useStoreApi,
  Position,
  Node,
  Edge,
  Connection,
  ReactFlowInstance,
  MarkerType,
} from "@xyflow/react";
import { v4 } from "uuid";
import "@xyflow/react/dist/style.css";
import { useDrop } from "react-dnd";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { createProcessModelParams } from "../../utils/createProcessModelJSon";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../services/state/store";
import {
  setEdgesDetails,
  setIsConnect,
  setNodesDetails,
  setProcessVariables,
  setSelectedProcessModel,
} from "../../services/state/Process-modular/processModular";
import { useSelector } from "react-redux";
import {
  fetchAllProcesses,
  fetchProcessByUuid,
} from "../../services/state/processes/processes-async-calls";
import { useParams } from "react-router-dom";
import { fetchProcessModelJson } from "../../services/state/Process-modular/processModular-async-calls";
import { fetchRules } from "../../services/state/rules/rules-async-calls";
import { Process } from "@/services/state/processes/processes-interface";
import {
  fetchProcessInstanceByUuid,
  triggerProcessExecution,
} from "../../services/state/monitor/monitor-async-calls";
import { setSelectedProcess } from "../../services/state/processes/processes";
import { getAxiosClient } from "../../services/HttpsService";

interface CustomNode extends Node {
  internals: {
    positionAbsolute: {
      x: number;
      y: number;
    };
  };
}

const flowKey = "example-flow";
const MIN_DISTANCE = 150;
const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

export const useProcessModular = ({
  isInstanceBased,
}: {
  isInstanceBased?: boolean | undefined;
}) => {
  const store = useStoreApi();
  const toast = useToast();

  const processVariables = useSelector(
    (state: RootState) => state.processModeler.processVariables
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const processVariablesRef = useRef<any[] | undefined>();
  const nodeIoDetailsRef = useRef<any[] | undefined>();
  const selectedProcessRef = useRef<Process | undefined>();
  const lastFetchedInstanceRef = useRef<string | null>(null);
  // const [isEdgeLabelModalOpen, setIsEdgeLabelModalOpen] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState<Edge | undefined>(undefined);
  const [modalCoordinates, setModalCoordinates] = useState({ x: 0, y: 0 });

  const [copiedNode, setCopiedNode] = useState<Node>();
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  const fallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { setViewport, getInternalNode } = useReactFlow();
  const dispatch: AppDispatch = useDispatch();
  const connect = useSelector(
    (state: RootState) => state.processModeler.isConnect
  );
  const {
    isOpen: isEdgeLabelModalOpen,
    onOpen: onEdgeLabelModalOpen,
    onClose: onEdgeLabelModalClose,
  } = useDisclosure();

  const { processId, processInstanceId } = useParams();

  const theme = useSelector(
    (state: RootState) => state.processModeler.selectedTheme
  );

  useEffect(() => {
    console.log("📋 Fetching process model for processId:", processId);
    dispatch(fetchProcessByUuid(processId as string));
    dispatch(fetchProcessModelJson(processId as string));
    // Enable connection handles on component mount
    dispatch(setIsConnect(true));
  }, []);

  const selectedProcess = useSelector(
    (state: RootState) => state.processes.selectedProcess
  );

  const selectedProcessModel = useSelector(
    (state: RootState) => state.processModeler.selecetedProcessModel
  );

  const selectedProcessModelJson = useSelector(
    (state: RootState) => state.processModeler.selectedProcessModelJson
  );

  const selectedNode = useSelector(
    (state: RootState) => state.processModeler.selectedNode
  );

  const nodeInputOutputDetails = useSelector(
    (state: RootState) => state.nodeIODetails.nodeIoDetails
  );

  const processInstanceInfo = useSelector(
    (state: RootState) => state.monitor.processInstanceInfo
  );

  const processInstanceNodeInfo = useSelector(
    (state: RootState) => state.monitor.processInstanceNodeInfo
  );

  const isFetchingProcessInstance = useSelector(
    (state: RootState) => state.monitor.isFetching
  );

  const processes = useSelector(
    (state: RootState) => state.processes.processes
  );

  const rules = useSelector((state: RootState) => state.rules.rules);

  useEffect(() => {
    dispatch(fetchRules(selectedProcess?.app.uuid as string));
    dispatch(fetchAllProcesses());
  }, [selectedProcess]);

  const ruleMatchers = rules.map((rule) => ({
    name: `rule!${rule.ruleNm.replace(/ /g, "_")}`,
    value: `rule!${rule.uuid}`,
  }));

  const pvMatchers = processVariablesRef.current?.map((pv) => ({
    name: `pv!${pv.varNm.replace(/ /g, "_")}`,
    value: `pv!${pv.uuid}`,
  }));

  const pmMatchers = processes.map((process) => ({
    name: `pm!${process.processNm.replace(/ /g, "_")}`,
    value: `"${process.uuid}"`,
  }));

  useEffect(() => {
    processVariablesRef.current = processVariables;
  }, [processVariables]);

  useEffect(() => {
    nodeIoDetailsRef.current = nodeInputOutputDetails;
  }, [nodeInputOutputDetails]);

  useEffect(() => {
    selectedProcessRef.current = selectedProcess;
  }, [selectedProcess]);

  useEffect(() => {
    // Clear any existing fallback timeout
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
    }

    if (isInstanceBased) {
      // For instance-based views, wait for process model, node info, and fetching to complete
      const hasValidExecutionData = processInstanceNodeInfo && Object.keys(processInstanceNodeInfo).length > 0;

      if (selectedProcessModelJson && hasValidExecutionData && !isFetchingProcessInstance) {
        console.log("✅ Restoring JSON with process instance data:", {
          nodeCount: Object.keys(processInstanceNodeInfo).length,
          executionNodes: Object.keys(processInstanceNodeInfo)
        });
        onJsonRestore();
      } else if (selectedProcessModelJson && !isFetchingProcessInstance) {
        console.log("⏳ Waiting for complete instance data:", {
          hasProcessModel: !!selectedProcessModelJson,
          hasExecutionData: hasValidExecutionData,
          isFetching: isFetchingProcessInstance,
          executionDataKeys: processInstanceNodeInfo ? Object.keys(processInstanceNodeInfo) : []
        });

        // Set fallback timeout to render without execution data after 3 seconds
        fallbackTimeoutRef.current = setTimeout(() => {
          console.log("⏰ Fallback: Rendering without execution data after timeout");
          onJsonRestore();
        }, 3000);
      }
    } else {
      // For regular design view, just need process model
      if (selectedProcessModelJson) {
        console.log("✅ Restoring JSON for design view");
        onJsonRestore();
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
    };
  }, [selectedProcessModelJson, processInstanceNodeInfo, isInstanceBased, isFetchingProcessInstance]);

  const handleLabelChange = (id: string, newLabel: string) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? { ...node, data: { ...node.data, label: newLabel } }
          : node
      )
    );
  };

  interface SubProcessDetails {
    subProcess?: string;
    expression?: string | null;
    isDynamic?: boolean;
    runAs: string;
    subProcessVariables: {
      inputVar: string | null;
      expression: string;
      isDynamic: boolean;
    }[];
    subProcessInputs?: string;
    subProcessOutputs: string[];
  }

  const handleNodeIoDetails = (
    id: string | undefined,
    nodeInfo: { name: string; description: string; displayName: string },
    ioDetails: any,
    gatewayDetails: { expression: string; nextNodeUuid: string }[] | undefined,
    nodeExecDetails:
      | {
          integrationUuid: string;
          inputs: string[];
          targetVariableUuid: string;
        }
      | undefined,
    subProcessDetails: SubProcessDetails | undefined
  ) => {
    const newNodes = nodes.map((node) =>
      node.id === id
        ? {
            ...node,
            data: {
              ...node.data,
              nodeIoDetails: ioDetails.map((io: any) => ({
                uuid: io.uuid,
                ioType: io.ioType,
                expression: expressionHandler(io.expression),
                target: io.target,
                isDefault: io.isDefault,
              })),
              gatewayDetails,
              nodeExecDetails,
              label: nodeInfo.name,
              name: nodeInfo.name,
              description: nodeInfo.description,
              displayName: nodeInfo.displayName,
              subProcessDetails: {
                subProcess: subProcessDetails?.subProcess,
                expression: subProcessDetails?.expression
                  ? expressionHandler(subProcessDetails.expression)
                  : null,
                isDynamic: subProcessDetails?.isDynamic || false,
                runAs: subProcessDetails?.runAs || "",
                subProcessVariables:
                  subProcessDetails?.subProcessVariables || [],
                subProcessInputs: subProcessDetails?.subProcessInputs
                  ? expressionHandler(subProcessDetails.subProcessInputs)
                  : null,
                subProcessOutputs: subProcessDetails?.subProcessOutputs || [],
              },
            },
          }
        : node
    );
    console.log("newNodes", newNodes);
    setNodes(newNodes);
    dispatch(setNodesDetails(newNodes));
  };

  const expressionHandler = (expression: string) => {
    console.log("expressionHandler", expression);

    const matchers = pvMatchers
      ? [...ruleMatchers, ...pvMatchers, ...pmMatchers]
      : [...ruleMatchers, ...pmMatchers];

    matchers.sort((a, b) => b.name.length - a.name.length);

    const placeholders: { placeholder: string; value: string }[] = [];

    matchers.forEach((match, index) => {
      const escapedName = match.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(escapedName, "g");
      const placeholder = `__REPLACEMENT_${index}__`;

      if (regex.test(expression)) {
        expression = expression.replace(regex, placeholder);
        placeholders.push({ placeholder, value: match.value });
      }
    });

    placeholders.forEach(({ placeholder, value }) => {
      const placeholderRegex = new RegExp(placeholder, "g");
      expression = expression.replace(placeholderRegex, value);
    });

    console.log("expressionHandlerAfter", expression);
    return expression;
  };

  const reverseExpressionHandler = (expression: string) => {
    const matchers = pvMatchers
      ? [...ruleMatchers, ...pvMatchers, ...pmMatchers]
      : [...ruleMatchers, ...pmMatchers];

    // Sort by value length descending to avoid partial matches
    matchers.sort((a, b) => b.value.length - a.value.length);

    matchers.forEach((match) => {
      const regex = new RegExp(
        match.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "g"
      ); // escape special regex chars
      expression = expression?.replace(regex, match.name);
    });

    return expression;
  };

  useEffect(() => {
    if (nodes.length > 0) {
      nodes.forEach((node) => {
        const subProcessDetails = node.data?.subProcessDetails as
          | SubProcessDetails
          | undefined;
        if (subProcessDetails && subProcessDetails.expression) {
          node = {
            ...node,
            data: {
              ...node.data,
              subProcessDetails: {
                ...subProcessDetails,
                expression: reverseExpressionHandler(
                  subProcessDetails.expression
                ),
              },
              nodeIoDetails: (node.data?.nodeIoDetails as any[])?.map(
                (io: any) => ({
                  ...io,
                  expression: reverseExpressionHandler(io.expression),
                })
              ),
            },
          };
        }
      });
      console.log("tst", nodes);
      setNodes((nds) => [...nds]);
      dispatch(setNodesDetails(nodes));
    }
  }, [processes]);

  const getClosestNodes = useCallback(
    (
      node: Node
    ): { leftNode: CustomNode | null; rightNode: CustomNode | null } => {
      const { nodeLookup } = store.getState();
      const internalNode = getInternalNode(node.id);

      let closestLeftNode: CustomNode | null = null;
      let closestRightNode: CustomNode | null = null;
      let closestLeftDistance = Number.MAX_VALUE;
      let closestRightDistance = Number.MAX_VALUE;

      Array.from(nodeLookup.values()).forEach((n: CustomNode) => {
        if (
          n.id !== internalNode?.id &&
          internalNode?.internals?.positionAbsolute
        ) {
          const dx =
            n.internals.positionAbsolute.x -
            internalNode.internals.positionAbsolute.x;
          const dy =
            n.internals.positionAbsolute.y -
            internalNode.internals.positionAbsolute.y;
          const d = Math.sqrt(dx * dx + dy * dy);

          if (dx < 0 && d < closestLeftDistance && d < MIN_DISTANCE) {
            closestLeftDistance = d;
            closestLeftNode = n;
          }

          if (dx > 0 && d < closestRightDistance && d < MIN_DISTANCE) {
            closestRightDistance = d;
            closestRightNode = n;
          }
        }
      });
      return { leftNode: closestLeftNode, rightNode: closestRightNode };
    },
    [getInternalNode, store]
  );

  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const { leftNode, rightNode } = getClosestNodes(node);

      if (leftNode && rightNode) {
        setEdges((es) =>
          es.filter(
            (e) => !(e.source === leftNode.id && e.target === rightNode.id)
          )
        );
        const leftEdge = {
          id: `e${leftNode.id}-${node.id}`,
          source: leftNode.id,
          target: node.id,
          style: {
            strokeWidth: 2,
            stroke: theme === "light" ? "#000000" : "#ffffff", // Dynamic color based on theme
          },
          type: "bizier",
        };
        const rightEdge = {
          id: `e${node.id}-${rightNode.id}`,
          source: node.id,
          target: rightNode.id,
          style: {
            strokeWidth: 2,
            stroke: theme === "light" ? "#000000" : "#ffffff", // Dynamic color based on theme
          },
          type: "bizier",
        };

        setEdges((es) => [...es, leftEdge, rightEdge]);
      }
      setNodes((nds) => {
        const draggedNodeIndex = nds.findIndex((n) => n.id === node.id);
        const newNodes = [...nds];

        if (leftNode && draggedNodeIndex > 0) {
          newNodes[draggedNodeIndex - 1].position.x = node.position.x - 250;
        }

        if (rightNode && draggedNodeIndex < nds.length - 1) {
          newNodes[draggedNodeIndex + 1].position.x = node.position.x + 250;
        }

        return newNodes;
      });
    },
    [getClosestNodes, setEdges, setNodes, theme]
  );

  const onAdd = useCallback(() => {
    const lastNode = nodes[nodes.length - 1];
    const newNodeId = v4();
    const newNodePositionX = lastNode.position.x + 250;

    const newNode: Node = {
      id: newNodeId,
      data: { label: "Added node" },
      position: {
        x: newNodePositionX,
        y: 0,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };

    setNodes((nds) => [...nds, newNode]);

    setEdges((eds) => {
      const { leftNode, rightNode } = getClosestNodes(newNode);

      if (leftNode && rightNode) {
        const updatedEdges = eds.filter(
          (e) => !(e.source === leftNode.id && e.target === rightNode.id)
        );

        return [
          ...updatedEdges,
          {
            id: `e${leftNode.id}-${newNode.id}`,
            source: leftNode.id,
            target: newNode.id,
          },
          {
            id: `e${newNode.id}-${rightNode.id}`,
            source: newNode.id,
            target: rightNode.id,
          },
        ];
      }

      return eds;
    });
  }, [nodes, setNodes, setEdges, getClosestNodes]);

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      const newEdge = {
        ...params,
        style: {
          strokeWidth: 2,
          stroke: theme === "light" ? "#000000" : "#ffffff", // Dynamic color based on theme
        },
        type: "bizier",
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 10,
          height: 10,
          color: theme === "light" ? "#000000" : "#ffffff", // Dynamic marker color based on theme
        },
        label: "",
        labelStyle: {
          fontSize: 16,
          transform: "translate(0px, -15px)",
          fill: theme === "light" ? "#000000" : "#ffffff", // Set label text color
        },
        labelBgStyle: { fill: "white", color: "black", fillOpacity: 0 },

        onClick: (event: React.MouseEvent, edge: Edge) => {
          event.stopPropagation();

          const labelText = typeof edge.label === "string" ? edge.label : "";

          const newLabel = prompt("Edit edge label:", labelText);
          if (newLabel !== null) {
            setEdges((eds) =>
              eds.map((e) => (e.id === edge.id ? { ...e, label: newLabel } : e))
            );
          }
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges, theme] // Add theme to the dependency array
  );

  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.stopPropagation();
      setSelectedEdge(edge);
      setModalCoordinates({ x: event.clientX, y: event.clientY });
      onEdgeLabelModalOpen();
    },
    [setEdges]
  );

  const handleEdgeSave = (newLabel: string) => {
    if (selectedEdge) {
      setEdges((eds) =>
        eds.map((e) =>
          e.id === selectedEdge.id ? { ...e, label: newLabel } : e
        )
      );
    }
  };

  const handleCopyNode = () => {
    if (selectedNode) {
      console.log("selectedNode", selectedNode);
      const nodeDetails = nodes.find((nd) => nd.id == selectedNode);
      setCopiedNode(nodeDetails);
      toast({
        title: "Node copied",
        status: "success",
        position: "top",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handlePasteNoded = useCallback(() => {
    const lastNode = nodes[nodes.length - 1];

    if (copiedNode) {
      const newNodeId = v4();
      const newNodePositionX = lastNode.position.x + 100;

      const newNode: Node = {
        ...copiedNode,
        id: newNodeId, // Assign a new ID

        position: {
          x: newNodePositionX,
          y: lastNode.position.y, // You might want to keep the same Y position or modify it
        },
      };

      setNodes((nds) => [...nds, newNode]);
    }
  }, [nodes, setNodes, setEdges, getClosestNodes, copiedNode]);

  const onSaveToDB = useCallback(async () => {
    if (rfInstance) {
      const flow = rfInstance.toObject();
      localStorage.setItem(flowKey, JSON.stringify(flow));
      const sortedNodes: Node[] = [...nodes].sort(
        (a, b) => a.position.x - b.position.x
      );

      const getNodeDetails = (id: string) => {
        return sortedNodes.find((node) => node.id === id);
      };

      const graph: {
        [key: string]: {
          node: {
            nodeUuid: string;
            nodeNm: string;
            nodeType: string;
            nodeIoDetails: any[];
            nodeExecDetails: any;
            gatewayDetails:
              | { expression: string; nextNodeUuid: string }[]
              | undefined;
            subProcessDetails:
              | {
                  subProcess?: string;
                  expression?: string | null;
                  isDynamic?: boolean;
                  runAs: string;
                  subProcessVariables: {
                    inputVar: string | null;
                    expression: string;
                    isDynamic: boolean;
                  }[];
                  subProcessInputs?: string;
                  subProcessOutputs: string[];
                }
              | undefined;
          };
          edges: { nodeUuid: string; nodeNm: string }[];
        };
      } = {};

      sortedNodes.forEach((node) => {
        graph[node.id] = {
          node: {
            nodeUuid: node.id,
            nodeNm: String(node.data.label),
            nodeType: String(node.data.nodeTypCd),
            nodeIoDetails: node.data.nodeIoDetails as any[],
            nodeExecDetails: node.data.nodeExecDetails,
            gatewayDetails: node.data.gatewayDetails as {
              expression: string;
              nextNodeUuid: string;
            }[],
            subProcessDetails: node.data.subProcessDetails as {
              subProcess?: string;
              expression?: string | null;
              isDynamic?: boolean;
              runAs: string;
              subProcessVariables: {
                inputVar: string | null;
                expression: string;
                isDynamic: boolean;
              }[];
              subProcessInputs?: string;
              subProcessOutputs: string[];
            },
          },
          edges: [],
        };
      });
      edges.forEach((edge) => {
        const sourceNode = getNodeDetails(edge.source);
        const targetNode = getNodeDetails(edge.target);

        if (sourceNode && targetNode) {
          graph[edge.source].edges.push({
            nodeUuid: edge.target,
            nodeNm: String(targetNode.data.label),
          });
        }
      });

      console.log("graph", graph);

      const nodeDetails = Object.keys(graph).reduce((acc, nodeId) => {
        acc[nodeId] = {
          node: {
            nodeUuid: graph[nodeId].node.nodeUuid,
            nodeNm: graph[nodeId].node.nodeNm,
            nodeTypCd: graph[nodeId].node.nodeType,
            nodeIoDetails: graph[nodeId].node.nodeIoDetails?.map((io) => ({
              uuid: io.uuid,
              ioType: io.ioType,
              expression: expressionHandler(io.expression),
              target: io.target,
              isDefault: io.isDefault,
            })),
            gatewayType:
              graph[nodeId].node.nodeType === "XOR_GW"
                ? "XOR"
                : graph[nodeId].node.nodeType === "OR_GW"
                ? "OR"
                : graph[nodeId].node.nodeType === "AND_GW"
                ? "AND"
                : undefined,
            gatewayDetails: graph[nodeId].node.gatewayDetails?.map(
              (gwd, index) => ({
                expression:
                  graph[nodeId].node.gatewayDetails?.length !== index + 1 &&
                  graph[nodeId].node.nodeType !== "AND_GW"
                    ? expressionHandler(gwd.expression)
                    : "exp!(return true)",
                nextNodeUuid: gwd.nextNodeUuid,
              })
            ),
            subProcessDetails: {
              subProcess:
                graph[nodeId].node.subProcessDetails?.subProcess || "",
              expression: graph[nodeId].node.subProcessDetails?.expression
                ? expressionHandler(
                    graph[nodeId].node.subProcessDetails?.expression as string
                  )
                : null,
              isDynamic:
                graph[nodeId].node.subProcessDetails?.isDynamic || false,
              runAs: graph[nodeId].node.subProcessDetails?.runAs || "",
              subProcessVariables:
                graph[nodeId].node.subProcessDetails?.subProcessVariables.map(
                  (spv) => ({
                    inputVar: spv.inputVar,
                    expression: expressionHandler(spv.expression),
                    isDynamic: spv.isDynamic,
                  })
                ) || [],
              subProcessOutputs:
                graph[nodeId].node.subProcessDetails?.subProcessOutputs || [],
            },
            nodeExecDetails: {
              integrationUuid:
                graph[nodeId].node.nodeExecDetails?.integrationUuid,
              inputs: graph[nodeId].node.nodeExecDetails?.inputs?.map(
                (inp: string) => expressionHandler(inp)
              ),
            },
          },
          edges: graph[nodeId].edges.length > 0 ? graph[nodeId].edges : [],
        };
        return acc;
      }, {} as { [key: string]: any });

      console.log("nodeDetails", nodeDetails, sortedNodes);

      const processNodes = sortedNodes.map((node) => {
        return {
          lane: "0",
          nodeTypeCode: node.data.nodeTypCd,
          id: node.id,
          icon: {
            id: node.data.image,
          },
          general: {
            name: {
              localeList: [
                {
                  locale: {
                    country: "US",
                    lang: "en",
                  },
                  value: node.data.label,
                },
              ],
            },
            displayName: {
              localeList: [
                {
                  locale: {
                    country: "US",
                    lang: "en",
                  },
                  value: node.data.displayName
                    ? node.data.displayName
                    : node.data.label,
                },
              ],
            },
            description: {
              localeList: [
                {
                  locale: {
                    country: "US",
                    lang: "en",
                  },
                  value: node.data.description ? node.data.description : "",
                },
              ],
            },
          },
          data: {
            name: node.data.label,
            "custom-params": "",
            acps: "",
            "output-exprs": "",
            nodeIoInfo: (node.data.nodeIoDetails as any[]).map((io) => ({
              uuid: io.uuid,
              ioType: io.ioType,
              expression: io.expression,
              target: io.target,
              isDefault: io.isDefault,
            })),
            gatewayDetails: (node.data.gatewayDetails as any[])?.map(
              (gateway: any, index: number) => {
                const gatewayArray = node.data.gatewayDetails as any[];
                const isLastItem = gatewayArray.length === index + 1;
                const isAndGateway = node.data.nodeType === "AND_GW";

                return {
                  ...gateway,
                  expression:
                    !isLastItem && !isAndGateway
                      ? expressionHandler(gateway.expression)
                      : "exp!(return true)",
                };
              }
            ),
            nodeExecDetails: {
              integrationUuid:
                graph[node.id]?.node?.nodeExecDetails?.integrationUuid ?? "",
              inputs:
                graph[node.id]?.node?.nodeExecDetails?.inputs?.map(
                  (inp: string) => expressionHandler(inp)
                ) ?? [],
              targetVariableUuid:
                graph[node.id]?.node?.nodeExecDetails?.targetVariableUuid ?? "",
            },
            subProcessDetails: {
              subProcess:
                (node.data.subProcessDetails as SubProcessDetails)
                  ?.subProcess || "",
              expression: (node.data.subProcessDetails as SubProcessDetails)
                ?.expression
                ? expressionHandler(
                    (node.data.subProcessDetails as SubProcessDetails)
                      .expression as string
                  )
                : null,
              isDynamic:
                (node.data.subProcessDetails as SubProcessDetails)?.isDynamic ||
                false,
              runAs:
                (node.data.subProcessDetails as SubProcessDetails)?.runAs || "",
              subProcessVariables: (
                node.data.subProcessDetails as SubProcessDetails
              )?.subProcessVariables
                ? (
                    node.data.subProcessDetails as SubProcessDetails
                  ).subProcessVariables.map((sv) => ({
                    inputVar: sv.inputVar,
                    expression: expressionHandler(sv.expression),
                    isDynamic: sv.isDynamic,
                  }))
                : [],
              subProcessInputs: (
                node.data.subProcessDetails as SubProcessDetails
              )?.subProcessInputs
                ? expressionHandler(
                    (node.data.subProcessDetails as SubProcessDetails)
                      .subProcessInputs as string
                  )
                : null,
              subProcessOutputs:
                (node.data.subProcessDetails as SubProcessDetails)
                  ?.subProcessOutputs || [],
            },
          },
          decision: node.data.decision || "",
          position: node.position,
          sourcePosition: node.sourcePosition,
          targetPosition: node.targetPosition,
          type: node.type,
          shape: node.data.shape,
          measured: node.measured,
          selected: node.selected || false,
        };
      });

      const roles = [
        {
          users: "",
          groupUuid: "273449ad-ec7e-4fcf-98a5-67e64308ee82",
          name: "ADMIN",
        },
      ];

      const pmdetails = {
        uuid: selectedProcessRef.current?.uuid || "",
        name: selectedProcessRef.current?.processNm || "",
        description: selectedProcessRef.current?.processDesc || "",
        displayName: expressionHandler(
          selectedProcessRef.current?.processDnm || ""
        ),
        version: "1",
      };

      console.log("pmdetails", pmdetails);

      const jsonData = createProcessModelParams(
        "versionUuid",
        "folderUuid",
        roles,
        processVariablesRef.current ? processVariablesRef.current : [],
        pmdetails,
        processNodes,
        edges
      );

      console.log("jsonData", jsonData);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API}/main/process-definition-new`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              processUuid: selectedProcessRef.current?.uuid,
              processDetails: pmdetails,
              // processNodes: nodeDetails,
              // processVariables: processVariablesRef.current,
              // processDetails: pmdetails,
              processJson: jsonData,
            }),
          }
        );

        if (response.status === 201) {
          toast({
            title: "Saved Successfully",
            description: "Successfully saved to the database.",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } else {
          console.error("Failed to save", response.statusText);
          toast({
            title: "Failed",
            description: "Failed to save",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error while saving nodes:", error);
      }
    }
  }, [rfInstance, nodes, edges, flowKey]);

  const onJsonRestore = useCallback(() => {
    try {
      dispatch(setIsConnect(true));
      const flow = JSON.parse(selectedProcessModelJson as string);
      console.log("onJsonRestore called with:", {
        isInstanceBased,
        hasProcessInstanceInfo: !!processInstanceInfo,
        hasProcessInstanceNodeInfo: !!processInstanceNodeInfo,
        processInstanceStatus: processInstanceInfo?.status,
        processInstanceNodeInfoKeys: processInstanceNodeInfo ? Object.keys(processInstanceNodeInfo) : [],
        flowExists: !!flow
      });
      dispatch(setSelectedProcessModel(flow));

      if (selectedProcess) {
        const meta = flow.processModelDtls.process_model.pm.meta;

        dispatch(
          setSelectedProcess({
            ...selectedProcess,
            processDnm: meta.displayName.localeList[0].value,
          })
        );
      }

      if (flow?.processModelDtls?.process_model?.pm) {
        // Debug: Log all node IDs in the visual workflow vs execution data
        const workflowNodeIds = flow.processModelDtls.process_model.pm.nodes.map((node: any) => node.id);
        const executionNodeIds = processInstanceNodeInfo ? Object.keys(processInstanceNodeInfo) : [];

        console.log("🔍 Node ID Debug:", {
          workflowNodeIds,
          executionNodeIds,
          workflowCount: workflowNodeIds.length,
          executionCount: executionNodeIds.length,
          matching: workflowNodeIds.filter((id: string) => executionNodeIds.includes(id)),
          workflowOnly: workflowNodeIds.filter((id: string) => !executionNodeIds.includes(id)),
          executionOnly: executionNodeIds.filter((id: string) => !workflowNodeIds.includes(id))
        });

        const transformedNodes =
          flow.processModelDtls.process_model.pm.nodes.map((node: any) => ({
            id: node.id,
            position: node.position,
            data: {
              label: node.general?.name?.localeList?.[0]?.value || "",
              displayName:
                node.general?.displayName?.localeList?.[0]?.value || "",
              description:
                node.general?.description?.localeList?.[0]?.value || "",
              image: node.icon.id,
              nodeTypCd: node.nodeTypeCode,
              shape: node.shape,
              id: node.id,
              onLabelChange: handleLabelChange,
              nodeIoDetails: node.data.nodeIoInfo,
              gatewayDetails: node.data.gatewayDetails,
              nodeExecDetails: node.data?.nodeExecDetails,
              subProcessDetails: {
                subProcess: node.data?.subProcessDetails?.subProcess,
                expression:
                  reverseExpressionHandler(
                    node.data?.subProcessDetails?.expression
                  ) || null,
                isDynamic: node.data?.subProcessDetails?.isDynamic || false,
                runAs: node.data?.subProcessDetails?.runAs || "",
                subProcessInputs:
                  reverseExpressionHandler(
                    node.data?.subProcessDetails?.subProcessInputs
                  ) || "",
                subProcessVariables:
                  node.data?.subProcessDetails?.subProcessVariables || [],
                subProcessOutputs:
                  node.data?.subProcessDetails?.subProcessOutputs || [],
              },
              nodeStatus: isInstanceBased
                ? node.nodeTypeCode === "START_EV" ||
                  (node.nodeTypeCode === "END_EV" &&
                    processInstanceInfo?.status === "completed")
                  ? "completed"
                  : processInstanceNodeInfo?.[node.id]?.node?.status
                : "",
            },
            type: node.type,
            sourcePosition: node.sourcePosition || "right",
            targetPosition: node.targetPosition || "left",
          }));

        // Transform edges from the JSON structure
        const transformedEdges =
          flow.processModelDtls.process_model.pm.edges.map((edge: any) => {
            const sourceNode = transformedNodes.find((node: any) => node.id == edge.source);
            const targetNode = transformedNodes.find((node: any) => node.id == edge.target);

            // Check if node IDs exist in execution data
            const sourceInExecutionData = processInstanceNodeInfo?.[edge.source];
            const targetInExecutionData = processInstanceNodeInfo?.[edge.target];

            const isSourceCompleted =
              sourceNode?.data.nodeTypCd === "START_EV"
                ? "completed"
                : sourceInExecutionData?.node?.status === "completed";

            // For edges TO end nodes, be more lenient - if source executed, show as executed path
            const isTargetCompleted =
              targetNode?.data.nodeTypCd === "END_EV"
                ? processInstanceInfo?.status === "completed" || isSourceCompleted
                : targetInExecutionData?.node?.status === "completed";

            // For edges FROM gateways, check if the specific path was taken
            const isGatewaySource = sourceNode?.data.nodeTypCd === "XOR_GW" ||
                                   sourceNode?.data.nodeTypCd === "OR_GW" ||
                                   sourceNode?.data.nodeTypCd === "AND_GW";

            const shouldShowAsExecuted = isSourceCompleted && (
              isTargetCompleted ||
              (isGatewaySource && targetInExecutionData) || // Gateway took this path
              targetNode?.data.nodeTypCd === "END_EV" // Path to end node
            );

            console.log(`Edge ${edge.id} (${edge.source} -> ${edge.target}):`, {
              sourceNodeType: sourceNode?.data.nodeTypCd,
              targetNodeType: targetNode?.data.nodeTypCd,
              sourceExistsInExecution: !!sourceInExecutionData,
              targetExistsInExecution: !!targetInExecutionData,
              isSourceCompleted,
              isTargetCompleted,
              sourceStatus: sourceInExecutionData?.node?.status,
              targetStatus: targetInExecutionData?.node?.status,
              processInstanceStatus: processInstanceInfo?.status,
              processInstanceNodeInfo: !!processInstanceNodeInfo,
              isGatewaySource,
              shouldShowAsExecuted,
              willBeBlue: isInstanceBased && shouldShowAsExecuted
            });

            return {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              sourceHandle: edge.sourceHandle,
              targetHandle: edge.targetHandle,
              style: isInstanceBased
                ? shouldShowAsExecuted
                  ? { strokeWidth: 2, stroke: "#7652F7" }
                  : {
                      strokeWidth: 2,
                      stroke: theme === "light" ? "#000000" : "#ffffff", // Theme-based color
                    }
                : {
                    strokeWidth: 2,
                    stroke: theme === "light" ? "#000000" : "#ffffff", // Theme-based color
                  },
              type: edge.type,
              markerEnd: isInstanceBased
                ? shouldShowAsExecuted
                  ? {
                      type: MarkerType.ArrowClosed,
                      width: 10,
                      height: 10,
                      color: "#7652F7",
                    }
                  : {
                      type: MarkerType.ArrowClosed,
                      width: 10,
                      height: 10,
                      color: theme === "light" ? "#000000" : "#ffffff", // Theme-based color
                    }
                : {
                    type: MarkerType.ArrowClosed,
                    width: 10,
                    height: 10,
                    color: theme === "light" ? "#000000" : "#ffffff", // Theme-based color
                  },
              label: edge.label,
              labelStyle: {
                ...(edge.labelStyle || {}),
                fill: theme === "light" ? "#000000" : "#ffffff", // <-- 👈 Dynamic label text color
              },
              labelBgStyle: edge.labelBgStyle,
            };
          });

        setNodes(transformedNodes);
        setEdges(transformedEdges);
        // dispatch(setNodeIoDetails(flow.processModelDtls.process_model.pm.node));
        dispatch(
          setProcessVariables(flow.processModelDtls.process_model.pm.pvs)
        );
        console.log("transformedNodes", transformedNodes);
        dispatch(setNodesDetails(transformedNodes));
        dispatch(setEdgesDetails(transformedEdges));

        const { x = 0, y = 0, zoom = 1 } = flow.viewport || {};
        setViewport({ x, y, zoom });
      } else {
        console.log("Invalid JSON structure: missing process model details");
      }
    } catch (error) {
      console.error("Error parsing JSON data:", error);
    }
  }, [setNodes, setEdges, setViewport, selectedProcessModelJson]);

  const onJsonRestore1 = useCallback(
    (jsonInput: string) => {
      try {
        dispatch(setIsConnect(true));
        const flow = JSON.parse(jsonInput); // Use input from UI
        dispatch(setSelectedProcessModel(flow));

        if (selectedProcess) {
          const meta = flow.processModelDtls.process_model.pm.meta;

          dispatch(
            setSelectedProcess({
              ...selectedProcess,
              processDnm: meta.displayName.localeList[0].value,
            })
          );
        }

        if (flow?.processModelDtls?.process_model?.pm) {
          const transformedNodes =
            flow.processModelDtls.process_model.pm.nodes.map((node: any) => ({
              id: node.id,
              position: node.position,
              data: {
                label: node.general?.name?.localeList?.[0]?.value || "",
                displayName:
                  node.general?.displayName?.localeList?.[0]?.value || "",
                description:
                  node.general?.description?.localeList?.[0]?.value || "",
                image: node.icon.id,
                nodeTypCd: node.nodeTypeCode,
                shape: node.shape,
                id: node.id,
                onLabelChange: handleLabelChange,
                nodeIoDetails: node.data.nodeIoInfo,
                gatewayDetails: node.data.gatewayDetails,
                nodeExecDetails: node.data?.nodeExecDetails,
                subProcessDetails: node.data?.subProcessDetails,
                nodeStatus: isInstanceBased
                  ? node.nodeTypeCode === "START_EV" ||
                    (node.nodeTypeCode === "END_EV" &&
                      processInstanceInfo?.status === "completed")
                    ? "completed"
                    : processInstanceNodeInfo?.[node.id]?.node?.status
                  : "",
              },
              type: node.type,
              sourcePosition: node.sourcePosition || "right",
              targetPosition: node.targetPosition || "left",
            }));

          const transformedEdges =
            flow.processModelDtls.process_model.pm.edges.map((edge: any) => {
              const isSourceCompleted =
                transformedNodes.find((node: any) => node.id == edge.source)
                  .data.nodeTypCd === "START_EV"
                  ? "completed"
                  : processInstanceNodeInfo?.[edge.source]?.node?.status ===
                    "completed";
              const isTargetCompleted =
                transformedNodes.find((node: any) => node.id == edge.target)
                  .data.nodeTypCd === "END_EV" &&
                processInstanceInfo?.status === "completed"
                  ? "completed"
                  : processInstanceNodeInfo?.[edge.target]?.node?.status ===
                    "completed";

              return {
                id: edge.id,
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle,
                targetHandle: edge.targetHandle,
                style: isInstanceBased
                  ? isSourceCompleted && isTargetCompleted
                    ? { strokeWidth: 2, stroke: "#7652F7" }
                    : { strokeWidth: 2, stroke: "black" }
                  : edge.style,
                type: edge.type,
                markerEnd: isInstanceBased
                  ? isSourceCompleted && isTargetCompleted
                    ? {
                        type: MarkerType.ArrowClosed,
                        width: 10,
                        height: 10,
                        color: "#7652F7",
                      }
                    : {
                        type: MarkerType.ArrowClosed,
                        width: 10,
                        height: 10,
                        color: "black",
                      }
                  : {
                      type: MarkerType.ArrowClosed,
                      width: 10,
                      height: 10,
                      color: theme === "light" ? "#000000" : "#ffffff",
                    },
                label: edge.label,
                labelStyle: {
                  ...(edge.labelStyle || {}),
                  fill: theme === "light" ? "#000000" : "#ffffff", // <-- 👈 Dynamic label text color
                },
                labelBgStyle: edge.labelBgStyle,
              };
            });

          setNodes(transformedNodes);
          setEdges(transformedEdges);
          dispatch(
            setProcessVariables(flow.processModelDtls.process_model.pm.pvs)
          );
          dispatch(setNodesDetails(transformedNodes));
          dispatch(setEdgesDetails(transformedEdges));

          const { x = 0, y = 0, zoom = 1 } = flow.viewport || {};
          setViewport({ x, y, zoom });
        }
      } catch (error) {
        console.error("Error parsing JSON data:", error);
      }
    },
    [
      setNodes,
      setEdges,
      setViewport,
      dispatch,
      selectedProcess,
      isInstanceBased,
      processInstanceInfo?.status,
      processInstanceNodeInfo,
      theme,
    ]
  );

  const onFileRestore = useCallback(
    (event?: React.ChangeEvent<HTMLInputElement>) => {
      const file = event?.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const flow = JSON.parse(reader.result as string);
            dispatch(setSelectedProcessModel(flow));

            if (flow?.processModelDtls?.process_model?.pm) {
              const transformedNodes =
                flow.processModelDtls.process_model.pm.nodes.map(
                  (node: any) => ({
                    id: node.id,
                    position: node.position,
                    data: {
                      label:
                        node.general?.name?.localeList?.[0]?.value ||
                        "Unnamed Node",
                      image: node.icon.id,
                      nodeTypCd: node.nodeTypeCode,
                      shape: node.shape,
                    },
                    type: node.type,
                    sourcePosition: node.sourcePosition || "right",
                    targetPosition: node.targetPosition || "left",
                  })
                );

              const transformedEdges =
                flow.processModelDtls.process_model.pm.edges.map(
                  (edge: any) => ({
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    sourceHandle: edge.sourceHandle,
                    targetHandle: edge.targetHandle,
                    style: edge.style,
                    type: edge.type,
                    markerEnd: edge.markerEnd,
                  })
                );

              setNodes(transformedNodes);
              setEdges(transformedEdges);
              dispatch(setNodesDetails(transformedEdges));
              dispatch(setEdgesDetails(transformedEdges));

              const { x = 0, y = 0, zoom = 1 } = flow.viewport || {};
            } else {
              console.error(
                "Invalid JSON structure: missing process model details"
              );
            }
          } catch (error) {
            console.error("Error parsing JSON file:", error);
          }
        };

        reader.readAsText(file);
      }
    },
    [setNodes, setEdges, setViewport, selectedProcessModel]
  );

  const checkNodes = () => {
    console.log("nodes", nodes);
    handleCopyNode();
  };

  const getShapeStyle = (shape: string): React.CSSProperties => {
    switch (shape) {
      case "square":
        return { width: 75, height: 75 };
      case "rectangle":
        return { width: 150, height: 75 };
      case "circle":
        return {
          borderRadius: "50%",
          width: 75,
          height: 75,
          textAlign: "center",
          lineHeight: "70px",
        };
      case "diamond":
        return {
          width: 75,
          height: 75,
          textAlign: "center",
          lineHeight: "75px",
        };
      default:
        return {};
    }
  };
  const [, drop] = useDrop(
    () => ({
      accept: "node",
      drop: (
        item: {
          id: string;
          label: string;
          shape: string;
          width: number;
          height: number;
          image: number;
          nodeTypCd: string;
        },
        monitor
      ) => {
        const offset = monitor.getClientOffset();
        const shapeStyle: React.CSSProperties = getShapeStyle(item.shape);
        if (offset) {
          const position = {
            x: offset.x - 250,
            y: offset.y - 250,
          };
          const newId = v4();
          const newNode: Node = {
            id: newId,
            data: {
              label: item.label,
              nodeTypCd: item.nodeTypCd,
              image: item.image,
              id: newId,
              shape: item.shape,
              onLabelChange: handleLabelChange,
              nodeIoDetails: [],
            },

            position,
            sourcePosition:
              Position.Right ||
              Position.Left ||
              Position.Top ||
              Position.Bottom,
            targetPosition:
              Position.Right ||
              Position.Left ||
              Position.Top ||
              Position.Bottom,
            type: "custom",
          };

          setNodes((nds) => [...nds, newNode]);
          dispatch(setIsConnect(true));
        } else {
          console.error("Drop offset is null");
        }
      },
    }),
    [setNodes, dispatch]
  );

  useEffect(() => {
    dispatch(setNodesDetails(nodes));
  }, [nodes]);

  useEffect(() => {
    dispatch(setEdgesDetails(edges));
  }, [edges]);

  useEffect(() => {
    dispatch(setIsConnect(connect));
  }, [connect]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "c") {
        handleCopyNode();
      }
      if (event.ctrlKey && event.key === "v") {
        handlePasteNoded();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleCopyNode, handlePasteNoded]);

  useEffect(() => {
    console.log("Process instance loading effect triggered:", {
      isInstanceBased,
      processId,
      processInstanceId,
      isFetchingProcessInstance
    });

    const currentInstanceKey = `${processId}-${processInstanceId}`;

    if (processId && processInstanceId && isInstanceBased && lastFetchedInstanceRef.current !== currentInstanceKey) {
      console.log("🔄 Dispatching process instance data fetch:", {
        processId,
        processInstanceId,
        fetchInstanceURL: `api/eza_app_process_instance/uuid/${processInstanceId}`,
        triggerExecutionURL: `/main/process-execution?processId=${processId}&instanceId=${processInstanceId}`
      });

      lastFetchedInstanceRef.current = currentInstanceKey;

      dispatch(
        fetchProcessInstanceByUuid({
          uuid: processInstanceId,
        })
      );
      dispatch(
        triggerProcessExecution({
          processId: processId,
          instanceId: processInstanceId,
        })
      );
    }
  }, [isInstanceBased, processId, processInstanceId]);

  useEffect(() => {
    // Update edges with new theme color
    setEdges((currentEdges) =>
      currentEdges.map((edge) => {
        // Create a new edge object with updated style and markerEnd
        const updatedEdge: Edge = {
          ...edge,
          style: {
            ...edge.style,
            strokeWidth: edge.style?.strokeWidth || 2,
            stroke: theme === "light" ? "#000000" : "#ffffff",
          },
          markerEnd: edge.markerEnd
            ? {
                type: MarkerType.ArrowClosed,
                width: 10,
                height: 10,
                color: theme === "light" ? "#000000" : "#ffffff",
              }
            : // {
              //   type: MarkerType.ArrowClosed,
              //   width: 10,
              //   height: 10,
              //   color: theme === "light" ? "#000000" : "#ffffff", // Dynamic marker color based on theme
              // },
              undefined,
        };

        return updatedEdge;
      })
    );
  }, [theme, setEdges]);

  const runProcess = async () => {
    const url = `/main/start?processId=${selectedProcess?.uuid}`;
    const data: { [key: string]: string } = {};

    processVariables.forEach((it) => {
      data[it.varNm as string] =
        ["ANY", "RT"].includes(it.dataTypeCd) && it.varDefVal
          ? JSON.parse(it.varDefVal)
          : it.varDefVal;
    });

    toast({
      title: "Pipeline Triggered",
      description: "Successfully triggered the pipeline.",
      position: "top",
      status: "info",
      duration: 3000,
      isClosable: true,
    });

    try {
      const response = await getAxiosClient().post(url, data);

      if (response.status === 200) {
        toast({
          title: "Process Run Successful",
          description: "The process has run successfully.",
          position: "top",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Process Failed",
          description:
            "Pipeline failed, please check in the monitor for more details.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error during process execution", error);
      toast({
        title: "Pipeline Failed",
        description:
          "An error occurred while triggering the process. Please check monitor.",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return {
    store,
    nodes,
    edges,
    rfInstance,
    drop,
    onAdd,
    onConnect,
    onSaveToDB,
    setViewport,
    setRfInstance,
    onNodesChange,
    onEdgesChange,
    onEdgeClick,
    onJsonRestore1,
    onFileRestore,
    onNodeDragStop,
    getClosestNodes,
    getInternalNode,
    getShapeStyle,
    handleLabelChange,
    handleNodeIoDetails,
    checkNodes,
    handleCopyNode,
    handlePasteNoded,
    isEdgeLabelModalOpen,
    onEdgeLabelModalClose,
    modalCoordinates,
    selectedEdge,
    handleEdgeSave,
    runProcess,
  };
};
